import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ChangePassword.css';
import MainLogo from '../../../ui/components/MainLogo';

const ChangePassword: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const navigate = useNavigate();

  const validateUserLogin = async (): Promise<boolean> => {
    const token = localStorage.getItem('token');

    if (!token) {
      setError('You must be logged in to change your password');
      setTimeout(() => navigate('/signin'), 2000);
      return false;
    }

    try {
      const response = await fetch('/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        setError('Session expired. Please sign in again.');
        localStorage.removeItem('token');
        setTimeout(() => navigate('/signin'), 2000);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error validating user login:', error);
      setError('Unable to verify authentication. Please try again.');
      return false;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      setIsValidating(true);
      const isValid = await validateUserLogin();
      setIsValidating(false);

      if (!isValid) {
        // User will be redirected by validateUserLogin
        return;
      }
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate user is still logged in
    const isValid = await validateUserLogin();
    if (!isValid) {
      return;
    }

    // Validate passwords
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    const token = localStorage.getItem('token');
    setIsLoading(true);

    try {
      const response = await fetch('/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          throw new Error(data.message || 'Failed to change password');
        } else {
          throw new Error('Server error occurred');
        }
      }

      setSuccess('Password changed successfully! Redirecting...');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'An error occurred while changing password');
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="change-password-container">
        <MainLogo nav={navigate} />
        <div className="change-password-box">
          <h2 className="change-password-title">Change Password</h2>
          <p className="change-password-description">
            Verifying authentication...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="change-password-container">
      <MainLogo nav={navigate} />
      <div className="change-password-box">
        <h2 className="change-password-title">Change Password</h2>
        <p className="change-password-description">
          Update your password to keep your account secure.
        </p>

        {error && <div className="change-password-error">{error}</div>}
        {success && <div className="change-password-success">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <input
              type="password"
              id="currentPassword"
              className="change-password-input"
              placeholder="Current Password"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                setError('');
                setSuccess('');
              }}
              required
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <input
              type="password"
              id="newPassword"
              className="change-password-input"
              placeholder="New Password (min. 8 characters)"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setError('');
                setSuccess('');
              }}
              required
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <input
              type="password"
              id="confirmPassword"
              className="change-password-input"
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setError('');
                setSuccess('');
              }}
              required
              disabled={isLoading}
            />
          </div>
          <button
            type="submit"
            className="change-password-button"
            disabled={isLoading}
          >
            {isLoading ? 'Changing Password...' : 'Change Password'}
          </button>
        </form>

        <p className="change-password-links">
          <a href="/">Back to Home</a>
        </p>
      </div>
    </div>
  );
};

export default ChangePassword;
